import React from "react";
import { Box, Button, Typography } from "@mui/material";
import chevronRight from "@assets/facilityNetwork/chevron-right.svg";
import type { NetworkCompositionCounts } from "./facilityNetworkOverviewUtils";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import { FN } from "./facilityNetworkTheme";

type Props = {
  counts: NetworkCompositionCounts;
  onManageRequests: () => void;
  onPendingClick?: () => void;
  onInviteClick?: () => void;
};

/** Figma 9086:67404 / 9407:77087 — Network Composition band */
export default function FacilityNetworkCompositionBar({
  counts,
  onManageRequests,
  onPendingClick,
  onInviteClick,
}: Props) {
  const declinedLabel =
    counts.declinedCount === 1 ? "Declined Request" : "Declined Requests";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        width: "100%",
        py: 1,
        boxShadow: FN.figmaCardShadow,
        borderRadius: FN.figmaCardRadius,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 16,
            fontWeight: 400,
            color: FN.muted,
            lineHeight: "22px",
            whiteSpace: "nowrap",
          }}
        >
          Network Composition
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.figmaNeutral,
                lineHeight: "20px",
              }}
            >
              {counts.linkedCount}
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                color: FN.figmaNeutral,
                lineHeight: "16px",
                letterSpacing: "0.03px",
              }}
            >
              Linked Facilities
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
                lineHeight: "20px",
              }}
            >
              {counts.pendingCount}
            </Typography>
            <Button
              onClick={onPendingClick}
              disableRipple
              sx={{
                textTransform: "none",
                minWidth: 0,
                minHeight: 24,
                px: 1,
                py: 0.75,
                color: FN.primary,
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                letterSpacing: "0.03px",
                lineHeight: "16px",
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              Pending Requests
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.figmaError,
                lineHeight: "20px",
              }}
            >
              {counts.declinedCount}
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                color: FN.figmaError,
                lineHeight: "16px",
                letterSpacing: "0.03px",
              }}
            >
              {declinedLabel}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
                lineHeight: "20px",
              }}
            >
              {counts.inviteCount}
            </Typography>
            <Button
              onClick={onInviteClick}
              disableRipple
              sx={{
                textTransform: "none",
                minWidth: 0,
                minHeight: 24,
                px: 1,
                py: 0.75,
                color: FN.primary,
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                letterSpacing: "0.03px",
                lineHeight: "16px",
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              Invite Needed
            </Button>
          </Box>
        </Box>
      </Box>

      <Button
        onClick={onManageRequests}
        endIcon={<FacilityNetworkFigmaIcon src={chevronRight} size={12} />}
        sx={{
          textTransform: "none",
          color: FN.primary,
          fontFamily: FN.fontFamily,
          fontSize: 12,
          fontWeight: 400,
          letterSpacing: "0.03px",
          px: 1.5,
          py: 0.75,
          minHeight: 24,
          "& .MuiButton-endIcon": { ml: 0.5 },
          "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
        }}
      >
        Manage in Requests
      </Button>
    </Box>
  );
}
